import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface FirestoreTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

// UI Member interface - this is what the UI will use
export interface Member {
  id?: string;
  name: string;
  email: string;
  phone?: string;
  whatsappNumber?: string;
  department?: string;
  jobTitle?: string;
  handler?: string;
  leaderRank?: string;
  leaderTitle?: string;
  joinDate?: Date;
  status: 'active' | 'inactive';
  cardId?: string;
  createdAt?: FirestoreTimestamp | Date | string;
  updatedAt?: FirestoreTimestamp | Date | string;
}

// Internal interfaces (hidden from UI) - these map to backend employee structure
interface BackendEmployee {
  id?: string;
  handler: string;
  contact_info: {
    contactName: string;
    primaryEmail: string;
    primaryPhone: string;
    whatsappNumber: string;
    department: string;
    jobTitle: string;
  };
   leaderRank?: string;
  leaderTitle?: string;
  createdAt?: FirestoreTimestamp | Date | string;
  updatedAt?: FirestoreTimestamp | Date | string;
}

interface CreateBackendEmployeeRequest {
  handler: string;
  contact_info: {
    contactName: string;
    primaryEmail: string;
    primaryPhone: string;
    whatsappNumber: string;
    department: string;
    jobTitle: string;
  };
   leaderRank?: string;
  leaderTitle?: string;
}

interface UpdateBackendEmployeeRequest {
  handler?: string;
  contact_info?: {
    contactName?: string;
    primaryEmail?: string;
    primaryPhone?: string;
    whatsappNumber?: string;
    department?: string;
    jobTitle?: string;
  };
   leaderRank?: string;
  leaderTitle?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MembersService {
  private readonly baseUrl = environment.apiUrl;

  // State subjects for members
  private membersSubject = new BehaviorSubject<Member[]>([]);
  public members$ = this.membersSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  public error$ = this.errorSubject.asObservable();

  constructor(private http: HttpClient, private authService: AuthService) {}

  // --- Helper Methods ---
  private getAuthHeaders(): HttpHeaders {
    const token = (this.authService as any).idToken;
    if (!token) {
      throw new Error('User not authenticated');
    }
    return new HttpHeaders({ 
      Authorization: `Bearer ${token}`, 
      'Content-Type': 'application/json' 
    });
  }

  private setError(err: any) {
    const message = err?.error?.error || err?.message || 'Unknown error';
    this.errorSubject.next(String(message));
  }

  private convertFirestoreTimestamp(timestamp: FirestoreTimestamp | Date | string | undefined): Date {
    if (!timestamp) return new Date();
    
    // If it's already a Date object
    if (timestamp instanceof Date) return timestamp;
    
    // If it's a string, parse it
    if (typeof timestamp === 'string') return new Date(timestamp);
    
    // If it's a Firestore timestamp with _seconds
    if (typeof timestamp === 'object' && '_seconds' in timestamp) {
      return new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
    }
    
    // Fallback
    return new Date();
  }

  // Convert backend employee response to UI member format
  private backendEmployeeToMember(backendEmployee: BackendEmployee): Member {
    console.log('[DEBUG] Converting backend employee to member:', backendEmployee);
    
    const member: Member = {
      id: backendEmployee.id,
      name: backendEmployee.contact_info.contactName,
      email: backendEmployee.contact_info.primaryEmail,
      phone: backendEmployee.contact_info.primaryPhone,
      whatsappNumber: backendEmployee.contact_info.whatsappNumber,
      department: backendEmployee.contact_info.department,
      jobTitle: backendEmployee.contact_info.jobTitle,
      handler: backendEmployee.handler,
      leaderTitle: backendEmployee.leaderTitle,
      leaderRank: backendEmployee.leaderRank,
      joinDate: this.convertFirestoreTimestamp(backendEmployee.createdAt),
      status: 'active',
      cardId: '',
      createdAt: backendEmployee.createdAt,
      updatedAt: backendEmployee.updatedAt
    };
    
    console.log('[DEBUG] Converted member:', member);
    return member;
  }

  // Convert UI member to backend employee request format
  private memberToBackendEmployeeRequest(member: Partial<Member>, handler?: string): CreateBackendEmployeeRequest | UpdateBackendEmployeeRequest {
    return {
      //for auto-generating handler
      //  handler: handler || member.handler || member.name?.toLowerCase().replace(/\s+/g, '_') || 'member',
      handler:  member.handler,
      contact_info: {
        contactName: member.name ,
        primaryEmail: member.email ,
        primaryPhone: member.phone ,
        whatsappNumber: member.whatsappNumber || member.phone ,
        department: member.department ,
        jobTitle: member.jobTitle 
      },
      leaderRank: member.leaderRank,
      leaderTitle: member.leaderTitle
    };
  }

  // --- Member API Methods ---

  /**
   * Fetch all members for a card
   */
  fetchMembers(cardId: string): void {
    if (!cardId) {
      this.errorSubject.next('cardId is required');
      return;
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    const headers = this.getAuthHeaders();

    console.log('[DEBUG] MembersService: Fetching members for card:', cardId);

    this.http.get<BackendEmployee[]>(`${this.baseUrl}/cards/${cardId}/employees`, { headers })
      .pipe(
        tap(backendEmployees => {
          console.log('[DEBUG] MembersService: Received backend employees:', backendEmployees);
          // Convert to members format for UI
          const members = backendEmployees.map(emp => this.backendEmployeeToMember(emp));
          console.log('[DEBUG] MembersService: Converted to members:', members);
          this.membersSubject.next(members);
        }),
        catchError(err => {
          console.error('[DEBUG] MembersService: HTTP ERROR:', err);
          this.setError(err);
          return of([]);
        }),
        finalize(() => {
          console.log('[DEBUG] MembersService: Request completed (loading = false)');
          this.loadingSubject.next(false);
        })
      ).subscribe();
  }

  /**
   * Create a new member
   */
  createMember(cardId: string, memberData: Partial<Member>): Observable<Member> {
    if (!cardId) return throwError(() => new Error('cardId is required'));
    
    const headers = this.getAuthHeaders();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    console.log('[DEBUG] MembersService: Creating member:', memberData);

    // Convert member to backend employee format
    const backendEmployeeData = this.memberToBackendEmployeeRequest(memberData) as CreateBackendEmployeeRequest;

    return this.http.post<BackendEmployee>(`${this.baseUrl}/cards/${cardId}/employees`, backendEmployeeData, { headers })
      .pipe(
        tap(newBackendEmployee => {
          console.log('[DEBUG] MembersService: Backend employee created:', newBackendEmployee);
          // Convert to member format and add to local state
          const newMember = this.backendEmployeeToMember(newBackendEmployee);
          const current = this.membersSubject.value;
          this.membersSubject.next([newMember, ...current]);
        }),
        map(backendEmployee => this.backendEmployeeToMember(backendEmployee)),
        catchError(err => {
          console.error('[DEBUG] MembersService: Create error:', err);
          this.setError(err);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSubject.next(false))
      );
  }

  /**
   * Update a member
   */
  updateMember(cardId: string, memberId: string, updateData: Partial<Member>): Observable<Member> {
    if (!cardId || !memberId) return throwError(() => new Error('cardId and memberId are required'));
    
    const headers = this.getAuthHeaders();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    console.log('[DEBUG] MembersService: Updating member:', memberId, updateData);

    // Convert member update to backend employee format
    const backendUpdateData = this.memberToBackendEmployeeRequest(updateData) as UpdateBackendEmployeeRequest;

    return this.http.put<BackendEmployee>(`${this.baseUrl}/cards/${cardId}/employees/${memberId}`, backendUpdateData, { headers })
      .pipe(
        tap(updatedBackendEmployee => {
          console.log('[DEBUG] MembersService: Backend employee updated:', updatedBackendEmployee);
          // Convert to member format and update local state
          const updatedMember = this.backendEmployeeToMember(updatedBackendEmployee);
          const current = this.membersSubject.value;
          const index = current.findIndex(member => member.id === memberId);
          if (index >= 0) {
            const updated = [...current];
            updated[index] = updatedMember;
            this.membersSubject.next(updated);
          }
        }),
        map(backendEmployee => this.backendEmployeeToMember(backendEmployee)),
        catchError(err => {
          console.error('[DEBUG] MembersService: Update error:', err);
          this.setError(err);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSubject.next(false))
      );
  }

  /**
   * Delete a member
   */
  deleteMember(cardId: string, memberId: string): Observable<{ success: boolean; message: string }> {
    if (!cardId || !memberId) return throwError(() => new Error('cardId and memberId are required'));
    
    const headers = this.getAuthHeaders();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    console.log('[DEBUG] MembersService: Deleting member:', memberId);

    return this.http.delete<{ success: boolean; message: string }>(`${this.baseUrl}/cards/${cardId}/employees/${memberId}`, { headers })
      .pipe(
        tap(result => {
          console.log('[DEBUG] MembersService: Member deleted:', result);
          // Remove from local state
          const current = this.membersSubject.value;
          const filtered = current.filter(member => member.id !== memberId);
          this.membersSubject.next(filtered);
        }),
        catchError(err => {
          console.error('[DEBUG] MembersService: Delete error:', err);
          this.setError(err);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSubject.next(false))
      );
  }

  // --- Additional Helper Methods ---

  /**
   * Add member (legacy method - use createMember instead)
   */
  async addMember(member: Omit<Member, 'id'>, cardId: string): Promise<string> {
    try {
      const result = await this.createMember(cardId, member).toPromise();
      return result?.id || '';
    } catch (error) {
      console.error('Error adding member:', error);
      throw error;
    }
  }

  /**
   * Import members (creates multiple members)
   */
    async importMembers(members: Omit<Member, 'id'>[], cardId: string): Promise<{ imported: Member[], errors: any[] }> {
    try {
      const headers = this.getAuthHeaders();
      const employees = members.map(member => this.memberToBackendEmployeeRequest(member));
      const payload = { employees };
  
      const response = await this.http.post<any>(
        `${this.baseUrl}/cards/${cardId}/employees/bulk`,
        payload,
        { headers }
      ).toPromise();
  
      let imported: Member[] = [];
      if (response && Array.isArray(response.employees)) {
        imported = response.employees.map((emp: BackendEmployee) => this.backendEmployeeToMember(emp));
        const current = this.membersSubject.value;
        this.membersSubject.next([...imported, ...current]);
      }
      // Return both imported and errors
      return { imported, errors: response.errors || [] };
    } catch (error) {
      console.error('Error importing members:', error);
      throw error;
    }
  }

  /**
   * Export members (returns current members)
   */
  exportMembers(): Member[] {
    return this.membersSubject.value;
  }

  /**
   * Get current members
   */
  getMembers(): Member[] {
    return this.membersSubject.value;
  }

  /**
   * Clear local state
   */
  clearState() {
    this.membersSubject.next([]);
    this.errorSubject.next(null);
    this.loadingSubject.next(false);
  }
}